import { useState, useEffect, useRef } from 'react'
import api, { getFileUrl } from '../utils/api'
import { sendMessageNotification } from '../utils/pwa'
import '../styles/chatArea.css'

export type ChatPartner =
  | {
      type: 'user'
      id: string
      email: string
      prenom?: string
      nom?: string
      statut?: string
      photoUrl?: string
    }
  | {
      type: 'group'
      id: string
      name?: string
      description?: string
      photoUrl?: string
    }

interface ChatAreaProps {
  selectedContact?: ChatPartner | null
  isMobileView?: boolean
  onGoBack?: () => void
  onMarkRead?: () => void
}

interface Message {
  id: string
  senderEmail: string
  recipientEmail: string
  message: string
  dateEnvoi: string
  isRead?: boolean
  status: string
  mediaType?: string
  filePath?: string
  fileName?: string
  fileOriginalName?: string
  fileMimeType?: string
  fileSize?: number
  senderName?: string // Pour les messages de groupe
}

interface GroupMember {
  email: string
  role: 'admin' | 'member'
  name?: string
  photoUrl?: string
}

export default function ChatArea({ selectedContact, isMobileView, onGoBack, onMarkRead }: ChatAreaProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [contactProfile, setContactProfile] = useState<any>(null)
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true)
  const [showGroupModal, setShowGroupModal] = useState(false)
  const [groupDetails, setGroupDetails] = useState<any>(null)
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([])
  const [newMembersToAdd, setNewMembersToAdd] = useState<string[]>([])
  const [allContacts, setAllContacts] = useState<any[]>([])
  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set())
  const [showMenuDropdown, setShowMenuDropdown] = useState(false)
  const [lastNotifiedMessageId, setLastNotifiedMessageId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const userEmail = localStorage.getItem('userEmail')

  // Auto-scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const markAsRead = async () => {
    if (!selectedContact || !userEmail) return

    try {
      if (selectedContact.type === 'user') {
        // Mark direct messages as read
        await api.post('/api/messages/mark-read', {
          otherEmail: selectedContact.email
        })
        console.log('✅ Marked direct messages as read:', selectedContact.email)
      } else if (selectedContact.type === 'group') {
        // Mark group messages as read
        await api.post('/api/group-messages/mark-read', {
          groupId: selectedContact.id
        })
        console.log('✅ Marked group messages as read:', selectedContact.id)
      }
      // Notify parent to refresh sidebar
      onMarkRead?.()
    } catch (err) {
      console.error('Error marking messages as read:', err)
    }
  }

  const isNearBottom = (): boolean => {
    if (!messagesContainerRef.current) return true
    const { scrollHeight, scrollTop, clientHeight } = messagesContainerRef.current
    const threshold = 100 // pixels from bottom
    return scrollHeight - (scrollTop + clientHeight) < threshold
  }

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      setShouldAutoScroll(isNearBottom())
    }
  }



  const getFileIcon = (mimeType?: string, fileName?: string) => {
    if (!mimeType && fileName) {
      const ext = fileName.split('.').pop()?.toLowerCase()
      if (ext === 'pdf') return '📄'
      if (['doc', 'docx'].includes(ext || '')) return '📝'
      if (['xls', 'xlsx', 'csv'].includes(ext || '')) return '📊'
      if (['ppt', 'pptx'].includes(ext || '')) return '📈'
      if (['zip', 'rar', '7z'].includes(ext || '')) return '🗜️'
      if (['txt', 'md'].includes(ext || '')) return '📃'
    }
    if (mimeType?.startsWith('image/')) return '🖼️'
    if (mimeType?.startsWith('video/')) return '🎬'
    if (mimeType?.startsWith('audio/')) return '🎧'
    if (mimeType?.includes('pdf')) return '📄'
    if (mimeType?.includes('word')) return '📝'
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊'
    if (mimeType?.includes('zip') || mimeType?.includes('compressed')) return '🗜️'
    return '📎'
  }

  // Gestion de la sélection
  const toggleMessageSelection = (messageId: string) => {
    const newSelection = new Set(selectedMessageIds)
    console.log('✓ Toggle sélection messageId:', messageId);
    console.log('  Avant:', Array.from(selectedMessageIds));
    
    if (newSelection.has(messageId)) {
      newSelection.delete(messageId)
      console.log('  Suppression de la sélection');
    } else {
      newSelection.add(messageId)
      console.log('  Ajout de la sélection');
    }
    
    console.log('  Après:', Array.from(newSelection));
    setSelectedMessageIds(newSelection)
  }

  const selectAllMessages = () => {
    setSelectedMessageIds(new Set(messages.map(m => m.id)))
  }

  const deselectAllMessages = () => {
    setSelectedMessageIds(new Set())
  }

  const deleteSelectedMessages = async () => {
    console.log('🗑️ deleteSelectedMessages() APPELÉE');
    console.log('  selectedMessageIds.size:', selectedMessageIds.size);
    
    if (selectedMessageIds.size === 0) {
      console.log('  ❌ Pas de messages sélectionnés, exit');
      return
    }

    if (!selectedContact) {
      console.log('  ❌ selectedContact est null, exit');
      return
    }
    
    const confirmed = window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedMessageIds.size} message(s) ?`)
    console.log('  Confirmation dialog:', confirmed);
    if (!confirmed) return

    try {
      const messageIdArray = Array.from(selectedMessageIds)
      const isGroup = selectedContact.type === 'group'
      
      console.log('🗑️ Suppression des messages:', messageIdArray);
      console.log('📊 État avant suppression:', { 
        totalMessages: messages.length,
        selectedIds: messageIdArray,
        isGroup,
        userEmail,
        contactId: selectedContact.id
      });
      
      if (isGroup) {
        // Supprimer les messages de groupe
        console.log('🗑️ Suppression de messages de groupe depuis:', `/api/groups/${selectedContact.id}/messages/delete-batch`);
        const response = await api.post(`/api/groups/${selectedContact.id}/messages/delete-batch`, {
          messageIds: messageIdArray,
          userEmail: userEmail
        })
        console.log('✅ Messages de groupe supprimés:', response.data)
      } else {
        // Supprimer les messages directs
        console.log('🗑️ Suppression de messages directs');
        const response = await api.post('/api/messages/delete-batch', {
          messageIds: messageIdArray,
          userEmail: userEmail
        })
        console.log('✅ Messages directs supprimés:', response.data)
      }

      // Retirer les messages de la liste localement
      const updatedMessages = messages.filter(m => {
        const shouldKeep = !selectedMessageIds.has(m.id);
        if (!shouldKeep) {
          console.log('🗑️ Suppression locale du message:', m.id);
        }
        return shouldKeep;
      });
      
      console.log('📊 État après suppression:', { 
        totalMessages: updatedMessages.length,
        removed: messages.length - updatedMessages.length
      });
      
      setMessages(updatedMessages)
      setSelectedMessageIds(new Set())
      setIsSelectionMode(false)
      setShowMenuDropdown(false)
    } catch (err: any) {
      console.error('❌ Erreur lors de la suppression:', err)
      alert('Erreur lors de la suppression des messages: ' + (err.response?.data?.error || err.message))
    }
  }

  const renderMessageBody = (msg: Message) => {
    const fileUrl = getFileUrl(msg.filePath)
    const hasFile = msg.filePath || msg.fileOriginalName || msg.fileName

    // Log de DEBUG pour chaque message
    console.log('📩 renderMessageBody called with:', {
      id: msg.id,
      message: msg.message,
      fileUrl,
      fileOriginalName: msg.fileOriginalName,
      fileName: msg.fileName,
      fileMimeType: msg.fileMimeType,
      mediaType: msg.mediaType
    })

    // Detectar o type basado em mediaType, fileMimeType ou extension
    let detectedMediaType = msg.mediaType;
    if (!detectedMediaType && msg.fileMimeType) {
      if (msg.fileMimeType.startsWith('image/')) {
        detectedMediaType = 'image';
      } else if (msg.fileMimeType.startsWith('video/')) {
        detectedMediaType = 'video';
      } else if (msg.fileMimeType.startsWith('audio/')) {
        detectedMediaType = 'audio';
      } else {
        detectedMediaType = 'file';
      }
    }

    // Fallback: detectar com base na extensão do arquivo se mediaType e fileMimeType não estão definidos
    if (!detectedMediaType && (msg.fileOriginalName || msg.fileName)) {
      const filename = msg.fileOriginalName || msg.fileName || '';
      const ext = filename.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) {
        detectedMediaType = 'image';
      } else if (['mp4', 'webm', 'avi', 'mov', 'mkv'].includes(ext || '')) {
        detectedMediaType = 'video';
      } else if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext || '')) {
        detectedMediaType = 'audio';
      } else {
        detectedMediaType = 'file';
      }
    }

    // Se o message PARECE um nome de arquivo de imagem, detectar também
    if (!detectedMediaType && msg.message) {
      const ext = msg.message.split('.').pop()?.toLowerCase();
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')) {
        console.log('🖼️ Message looks like image filename, treating as image:', msg.message);
        detectedMediaType = 'image';
      }
    }

    // ===== IMAGES UNIQUEMENT =====
    if (detectedMediaType === 'image') {
      const filename = msg.fileOriginalName || msg.fileName || msg.message || 'Image';
      
      console.log('🖼️ IMAGE DETECTED - Will render image, not text:', { 
        fileUrl, 
        filePath: msg.filePath,
        fileOriginalName: msg.fileOriginalName,
        fileName: msg.fileName,
        fileMimeType: msg.fileMimeType,
        mediaType: msg.mediaType,
        message: msg.message
      });
      
      // Essayer plusieurs URLs possibles
      let imageSrc = fileUrl;
      
      if (!imageSrc && msg.message) {
        // Si pas de filePath mais message contient peut-être un chemin
        imageSrc = getFileUrl(msg.message);
      }
      
      if (!imageSrc) {
        // Construire à partir du message ou du nom du fichier
        imageSrc = msg.message || filename;
        if (!imageSrc.startsWith('/') && !imageSrc.startsWith('http')) {
          imageSrc = `/${imageSrc}`;
        }
      }

      console.log('🖼️ Using image source:', imageSrc);

      return (
        <div className="message-media-wrapper">
          <img 
            src={imageSrc} 
            alt={filename}
            className="message-media"
            onError={(e) => {
              console.error('❌ Image failed to load from:', imageSrc, 'trying fallback...');
              const img = e.target as HTMLImageElement;
              
              // Essayer plusieurs fallbacks
              if (imageSrc.includes('r2.mboa.io')) {
                // Si c'était R2, essayer avec /api/files/
                const cleanPath = imageSrc.replace('https://r2.mboa.io/', '');
                img.src = `/api/files/${cleanPath}`;
                console.log('🔄 Fallback 1 - Trying /api/files/:', img.src);
              } else if (!img.src.includes('/files/')) {
                // Essayer avec /files/ prefix
                img.src = `/files/${imageSrc.replace(/^\//, '')}`;
                console.log('🔄 Fallback 2 - Trying /files/:', img.src);
              } else {
                // Dernier recours - afficher une image placeholder
                console.log('❌ All fallbacks failed for image');
                img.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22%3E%3Crect fill=%22%23ddd%22 width=%22200%22 height=%22200%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.3em%22 fill=%22%23999%22 font-family=%22sans-serif%22%3EImage Not Found%3C/text%3E%3C/svg%3E';
              }
            }}
          />
        </div>
      )
    }

    // ===== VIDEOS =====
    if (detectedMediaType === 'video' && fileUrl) {
      return (
        <div className="message-media-wrapper">
          <video controls className="message-media">
            <source src={fileUrl} type={msg.fileMimeType || 'video/mp4'} />
            Votre navigateur ne supporte pas la lecture de cette vidéo.
          </video>
        </div>
      )
    }

    // ===== AUDIO =====
    if (detectedMediaType === 'audio' && fileUrl) {
      return (
        <div className="message-media-wrapper">
          <audio controls className="message-media" src={fileUrl} />
        </div>
      )
    }

    // ===== AUTRES FICHIERS =====
    if (detectedMediaType === 'file' && (fileUrl || hasFile)) {
      return (
        <div className="message-file">
          <a href={fileUrl} target="_blank" rel="noreferrer" download={msg.fileOriginalName || msg.fileName || undefined}>
            <div className="file-info">
              <span className="file-icon">{getFileIcon(msg.fileMimeType, msg.fileOriginalName || msg.fileName)}</span>
              <div className="file-details">
                <strong>{msg.fileOriginalName || msg.fileName || 'Télécharger le fichier'}</strong>
                {msg.fileSize != null && <span>{(msg.fileSize / 1024).toFixed(1)} KB</span>}
              </div>
            </div>
            <span className="file-action">Télécharger</span>
          </a>
        </div>
      )
    }

    // ===== TEXTE SIMPLE (PAR DÉFAUT) =====
    // Afficher le texte du message si disponible (même si mediaType était détecté mais sans URL)
    if (msg.message) {
      console.log('💬 Rendering text message:', msg.message);
      return <p>{msg.message}</p>
    }

    // Pas de contenu
    console.log('📭 No content to render for message:', msg.id);
    return <p></p>
  }

  const formatMessageTime = (dateEnvoi: string | null | undefined): string => {
    if (!dateEnvoi) {
      return 'Maintenant'
    }

    try {
      // Essayer de parser la date
      let date: Date;
      
      // Si c'est une timestamp numérique
      if (typeof dateEnvoi === 'string' && /^\d+$/.test(dateEnvoi)) {
        date = new Date(parseInt(dateEnvoi))
      } 
      // Si c'est une chaîne ISO ou autre format
      else if (typeof dateEnvoi === 'string') {
        // Remplacer l'espace par T pour les formats DATETIME SQLite
        const normalizedDate = dateEnvoi.replace(' ', 'T')
        date = new Date(normalizedDate)
      } 
      else {
        return 'Maintenant'
      }

      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return 'Maintenant'
      }

      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      // Vérifier si le message est d'aujourd'hui
      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      // Vérifier si le message est d'hier
      else if (date.toDateString() === yesterday.toDateString()) {
        return 'Hier ' + date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })
      }
      // Pour les autres dates
      else {
        return date.toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }) + ' ' + date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error, dateEnvoi)
      return 'Maintenant'
    }
  }

  useEffect(() => {
    if (shouldAutoScroll) {
      scrollToBottom()
    }
  }, [messages, shouldAutoScroll])

  const loadMessages = async () => {
    if (!selectedContact || !userEmail) return

    try {
      let response

      if (selectedContact.type === 'group') {
        response = await api.get(`/api/groups/${selectedContact.id}/messages`)
        console.log('📨 Group messages loaded:', {
          userEmail,
          messagesCount: response.data?.length,
          firstMessage: response.data?.[0],
          senderEmails: response.data?.map((m: Message) => m.senderEmail)
        })
      } else {
        response = await api.get('/api/messages', {
          params: {
            email: userEmail,
            contactEmail: selectedContact.email
          }
        })
      }

      // Le backend filtre déjà les messages supprimés (is_deleted = 0)
      // Donc pas besoin de filtrer au frontend
      const allMessages = response.data || []
      
      console.log('📊 Messages chargés:', {
        count: allMessages.length,
        sample: allMessages[0]
      })
      
      // Détécter les nouveaux messages et envoyer des notifications
      if (allMessages.length > 0 && messages.length > 0) {
        const newMessages = allMessages.filter((msg: Message) => 
          !messages.some(m => m.id === msg.id) && msg.senderEmail !== userEmail
        )
        
        newMessages.forEach((msg: Message) => {
          if (lastNotifiedMessageId !== msg.id) {
            const senderName = selectedContact.type === 'group' 
              ? msg.senderName || msg.senderEmail 
              : selectedContact.prenom || selectedContact.email
            
            const messagePreview = msg.message || '[Fichier]'
            const notificationType = selectedContact.type === 'group' ? 'group' : 'direct'
            
            sendMessageNotification(senderName, messagePreview, notificationType, {
              url: window.location.href
            })
            
            setLastNotifiedMessageId(msg.id)
          }
        })
      }
      
      setMessages(allMessages)
    } catch (err: any) {
      console.error('Erreur lors du chargement des messages :', err)
    }
  }

  const loadContactProfile = async () => {
    if (!selectedContact || selectedContact.type === 'group') return
    try {
      const response = await api.get('/api/profile', {
        params: { email: selectedContact.email }
      })
      setContactProfile(response.data)
    } catch (err: any) {
      console.error('Erreur lors du chargement du profil du contact :', err)
    }
  }

  const loadGroupDetails = async () => {
    if (!selectedContact || selectedContact.type !== 'group') return
    try {
      const response = await api.get(`/api/groups/${selectedContact.id}`)
      console.log('📋 Group details response:', response.data)
      setGroupDetails(response.data)
      setGroupMembers(response.data.members || [])
      console.log('👥 Group members set to:', response.data.members || [])
    } catch (err: any) {
      console.error('Erreur lors du chargement des détails du groupe :', err)
    }
  }

  const loadAllContacts = async () => {
    try {
      const response = await api.get('/api/contacts', {
        params: { email: userEmail }
      })
      setAllContacts(Array.isArray(response.data) ? response.data : [])
    } catch (err: any) {
      console.error('Erreur lors du chargement des contacts :', err)
    }
  }

  const handleAddMembers = async () => {
    if (!selectedContact || selectedContact.type !== 'group' || newMembersToAdd.length === 0) return
    try {
      await api.post(`/api/groups/${selectedContact.id}/members`, {
        emails: newMembersToAdd,
        requestedBy: userEmail
      })
      setNewMembersToAdd([])
      await loadGroupDetails()
    } catch (err: any) {
      console.error('Erreur lors de l\'ajout de membres :', err)
    }
  }

  const handleRemoveMember = async (email: string) => {
    if (!selectedContact || selectedContact.type !== 'group') return
    try {
      await api.delete(`/api/groups/${selectedContact.id}/members/${email}`, {
        data: { requestedBy: userEmail }
      })
      await loadGroupDetails()
    } catch (err: any) {
      console.error('Erreur lors de la suppression du membre :', err)
    }
  }

  const handleLeaveGroup = async () => {
    if (!selectedContact || selectedContact.type !== 'group') return
    try {
      await api.post(`/api/groups/${selectedContact.id}/leave`, {
        userEmail
      })
      setShowGroupModal(false)
      // Rediriger ou nettoyer l'état du chat
      window.location.href = '/home'
    } catch (err: any) {
      console.error('Erreur lors de la quitte du groupe :', err)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    if (!file) {
      setSelectedFile(null)
      setFilePreviewUrl(null)
      return
    }

    setSelectedFile(file)
    const previewUrl = URL.createObjectURL(file)
    setFilePreviewUrl(previewUrl)
  }

  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl)
      }
    }
  }, [filePreviewUrl])

  // Charger les messages quand un contact est sélectionné
  useEffect(() => {
    if (!selectedContact) return

    setShouldAutoScroll(true) // Reset to auto-scroll when switching contacts
    setShowGroupModal(false) // Fermer le modal si on change de contact
    loadMessages()
    loadContactProfile()
    markAsRead() // Mark messages as read when opening a conversation
    if (selectedContact.type === 'group') {
      loadGroupDetails()
      loadAllContacts()
    }
    // Polling pour les nouveaux messages
    const interval = setInterval(loadMessages, 2000)
    return () => clearInterval(interval)
  }, [selectedContact, userEmail])

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedContact || !userEmail) return

    try {
      setLoading(true)
      let response

      if (selectedContact.type === 'group') {
        // Support pour fichiers en groupes
        if (selectedFile) {
          const formData = new FormData()
          formData.append('senderEmail', userEmail)
          formData.append('message', newMessage.trim())
          formData.append('file', selectedFile)

          response = await api.post(`/api/groups/${selectedContact.id}/messages`, formData)
        } else {
          response = await api.post(`/api/groups/${selectedContact.id}/messages`, {
            senderEmail: userEmail,
            message: newMessage.trim()
          })
        }
      } else if (selectedFile) {
        const formData = new FormData()
        formData.append('senderEmail', userEmail)
        formData.append('recipientEmail', selectedContact.email)
        formData.append('message', newMessage.trim())
        formData.append('file', selectedFile)

        response = await api.post('/api/messages/send', formData)
      } else {
        response = await api.post('/api/messages/send', {
          senderEmail: userEmail,
          recipientEmail: selectedContact.email,
          message: newMessage.trim()
        })
      }

      let newMessageItem: Message

      // Pour les messages de groupe, utiliser la réponse complète du serveur
      if (selectedContact.type === 'group' && response.data?.message) {
        console.log('📡 Response from backend (group message):', JSON.stringify(response.data, null, 2));
        newMessageItem = response.data.message
        console.log('📝 newMessageItem (after extraction):', JSON.stringify(newMessageItem, null, 2));
      } else {
        // Pour les messages directs
        console.log('📡 Response from backend (direct message):', JSON.stringify(response.data, null, 2));
        newMessageItem = response.data
      }

      console.log('✅ Final message to add to list:', JSON.stringify(newMessageItem, null, 2));
      setMessages([...messages, newMessageItem])
      setNewMessage('')
      setSelectedFile(null)
      setFilePreviewUrl(null)
      setShouldAutoScroll(true) // Always scroll to bottom when sending a message
    } catch (err: any) {
      console.error('Erreur lors de l\'envoi du message :', err)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  if (!selectedContact) {
    return (
      <div className="chat-area empty">
        <div className="empty-state">
          <p>Sélectionnez un contact pour commencer une conversation</p>
        </div>
      </div>
    )
  }

  const isGroupChat = selectedContact.type === 'group'
  const displayName = isGroupChat
    ? selectedContact.name || 'Groupe'
    : `${selectedContact.prenom || ''} ${selectedContact.nom || ''}`.trim() || selectedContact.email
  const subtitle = isGroupChat
    ? selectedContact.description || 'Discussion de groupe'
    : selectedContact.statut === 'online'
      ? '🟢 En ligne'
      : '⚫ Hors ligne'

  return (
    <div className="chat-area">
      {/* Header */}
      <div className="chat-header">
        {isMobileView && onGoBack && (
          <button 
            className="btn-go-back"
            onClick={onGoBack}
            title="Retour"
          >
            ← Retour
          </button>
        )}
        <div className="header-content">
          <div className="contact-avatar-header">
            <img
              src={getFileUrl(contactProfile?.photoUrl || selectedContact.photoUrl) || 'https://via.placeholder.com/40?text=User'}
              alt={displayName}
              className="contact-avatar"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/40?text=User';
              }}
            />
          </div>
          <div className="contact-info">
            <h3>{displayName}</h3>
            <span className={`status ${isGroupChat ? 'group' : selectedContact.statut}`}>
              {subtitle}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {isGroupChat && (
            <button 
              className="btn-group-info"
              onClick={async () => {
                await loadGroupDetails()
                setShowGroupModal(true)
              }}
              title="Détails du groupe"
            >
              ℹ️
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button 
              className="btn-menu"
              onClick={() => setShowMenuDropdown(!showMenuDropdown)}
              title="Menu"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '5px 10px'
              }}
            >
              ⋮
            </button>
            {showMenuDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '180px'
              }}>
                <button
                  onClick={() => {
                    setIsSelectionMode(!isSelectionMode)
                    if (!isSelectionMode) {
                      setSelectedMessageIds(new Set())
                    }
                    setShowMenuDropdown(false)
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 15px',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {isSelectionMode ? '❌ Quitter sélection' : '✓ Sélectionner'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barre de sélection */}
      {isSelectionMode && messages.length > 0 && (
        <div style={{
          background: '#f5f5f5',
          padding: '10px 15px',
          borderBottom: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{ fontSize: '14px', fontWeight: '500' }}>
            {selectedMessageIds.size} message(s) sélectionné(s)
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={selectAllMessages}
              style={{
                padding: '8px 12px',
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Tous
            </button>
            <button
              onClick={deselectAllMessages}
              style={{
                padding: '8px 12px',
                background: '#999',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Aucun
            </button>
            <button
              onClick={() => {
                console.log('🗑️ CLIC sur le bouton Supprimer!');
                console.log('📊 Avant appel deleteSelectedMessages:', {
                  selectedMessageIds: Array.from(selectedMessageIds),
                  userEmail,
                  isSelectionMode,
                  contactId: selectedContact.id,
                  contactType: selectedContact.type
                });
                deleteSelectedMessages();
              }}
              disabled={selectedMessageIds.size === 0}
              style={{
                padding: '8px 12px',
                background: selectedMessageIds.size === 0 ? '#ccc' : '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedMessageIds.size === 0 ? 'not-allowed' : 'pointer',
                fontSize: '12px'
              }}
            >
              🗑️ Supprimer
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="messages-container" ref={messagesContainerRef} onScroll={handleScroll}>
        {messages.length === 0 ? (
          <div className="empty-state">
            <p>Aucun message pour le moment. Commencez la conversation!</p>
          </div>
        ) : (
          messages.map((msg) => {
            // Normaliser les emails pour la comparaison (minuscules, sans espaces)
            const normalizedUserEmail = userEmail?.toLowerCase().trim() || '';
            const normalizedSenderEmail = msg.senderEmail?.toLowerCase().trim() || '';
            
            const isSent = normalizedSenderEmail === normalizedUserEmail;
            const isReceived = !isSent;
            const isGroupMsg = isGroupChat;
            
            console.log('📧 Message classification:', {
              msgId: msg.id,
              userEmail: normalizedUserEmail,
              senderEmail: normalizedSenderEmail,
              isSent,
              isReceived,
              isGroupMsg
            });
            
            return (
              <div
                key={msg.id}
                className={`message ${isSent ? 'sent' : 'received'} ${isGroupMsg ? 'group-message' : 'direct-message'}`}
              >
                {/* Checkbox en mode sélection */}
                {isSelectionMode && (
                  <input
                    type="checkbox"
                    checked={selectedMessageIds.has(msg.id)}
                    onChange={() => toggleMessageSelection(msg.id)}
                    style={{
                      marginRight: '8px',
                      cursor: 'pointer',
                      width: '18px',
                      height: '18px',
                      flexShrink: 0
                    }}
                  />
                )}
                
                {/* Pour les messages de groupe reçus, afficher le nom de l'expéditeur */}
                {isGroupMsg && isReceived && (
                  <div className="message-sender">{msg.senderName || msg.senderEmail}</div>
                )}
                
                {/* Contenu du message */}
                <div className="message-content">
                  {renderMessageBody(msg)}
                  {msg.mediaType && msg.mediaType !== 'text' && msg.message && (
                    <p className="message-caption">{msg.message}</p>
                  )}
                  <span className="message-time">
                    {formatMessageTime(msg.dateEnvoi)}
                  </span>
                </div>
                
                {/* Statut du message (seulement pour les messages envoyés) */}
                {isSent && (
                  <span className="message-status">
                    {msg.status === 'read' ? '✓✓' : '✓'}
                  </span>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="chat-input">
        <label className="file-upload-button">
          📎
          <input
            type="file"
            accept="*/*"
            onChange={handleFileChange}
          />
        </label>
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Écrivez un message..."
          rows={3}
        />
        <button
          onClick={handleSendMessage}
          disabled={loading || (!newMessage.trim() && !selectedFile)}
          className="btn-send"
        >
          {loading ? '...' : '✈️'}
        </button>
      </div>

      {/* File Preview */}
      {selectedFile && (
        <div className="file-preview-container">
          <div className="file-preview">
            <span>{selectedFile.name} • {(selectedFile.size / 1024).toFixed(1)} KB</span>
            <button
              className="btn-clear-file"
              onClick={() => {
                setSelectedFile(null)
                setFilePreviewUrl(null)
              }}
              title="Supprimer le fichier"
            >
              ✕
            </button>
          </div>
          {filePreviewUrl && selectedFile.type.startsWith('image/') && (
            <img src={filePreviewUrl} alt={selectedFile.name} className="file-preview-thumb" />
          )}
          {filePreviewUrl && selectedFile.type.startsWith('video/') && (
            <video controls className="file-preview-media">
              <source src={filePreviewUrl} type={selectedFile.type} />
              Votre navigateur ne supporte pas la lecture de cette vidéo.
            </video>
          )}
          {filePreviewUrl && selectedFile.type.startsWith('audio/') && (
            <audio controls className="file-preview-media" src={filePreviewUrl} />
          )}
        </div>
      )}

      {/* Group Info Modal */}
      {showGroupModal && isGroupChat && (
        <div className="group-info-overlay">
          <div className="group-info-modal">
            <div className="group-info-header">
              <h2>{groupDetails?.name || 'Détails du groupe'}</h2>
              <button className="btn-close-modal" onClick={() => setShowGroupModal(false)}>✕</button>
            </div>

            <div className="group-info-body">
              {groupDetails?.description && (
                <div className="group-info-section">
                  <h3>Description</h3>
                  <p className="group-description">{groupDetails.description}</p>
                </div>
              )}
              
              <div className="group-info-section">
                <h3>Membres ({groupMembers.length})</h3>
                <div className="members-list">
                  {groupMembers && groupMembers.length > 0 ? (
                    groupMembers.map((member) => (
                      <div key={member.email} className="member-card">
                        <div className="member-avatar">
                          <img
                            src={getFileUrl(member.photoUrl) || 'https://via.placeholder.com/32?text=User'}
                            alt={member.name || member.email}
                            className="avatar-img"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/32?text=User';
                            }}
                          />
                        </div>
                        <div className="member-info">
                          <span className="member-email">{member.name || member.email}</span>
                          {member.role === 'admin' && <span className="member-badge">Créateur</span>}
                        </div>
                        {groupDetails?.createdBy === userEmail && member.email !== userEmail && (
                          <button
                            className="btn-remove-member"
                            onClick={() => handleRemoveMember(member.email)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="no-members">Aucun membre à afficher</p>
                  )}
                </div>
              </div>

              {groupDetails?.createdBy === userEmail && (
                <div className="group-info-section">
                  <h3>Ajouter des membres</h3>
                  <div className="add-members-form">
                    {allContacts
                      .filter((c) => !groupMembers.some((m) => m.email === c.email))
                      .map((contact) => (
                        <label key={contact.email} className="add-member-checkbox">
                          <input
                            type="checkbox"
                            checked={newMembersToAdd.includes(contact.email)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewMembersToAdd([...newMembersToAdd, contact.email])
                              } else {
                                setNewMembersToAdd(newMembersToAdd.filter((e) => e !== contact.email))
                              }
                            }}
                          />
                          <span>{contact.prenom} {contact.nom} ({contact.email})</span>
                        </label>
                      ))}
                    {newMembersToAdd.length > 0 && (
                      <button className="btn-add-selected" onClick={handleAddMembers}>
                        Ajouter ({newMembersToAdd.length})
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="group-info-section">
                <button
                  className="btn-leave-group"
                  onClick={handleLeaveGroup}
                >
                  Quitter le groupe
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}